import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import { LOGO_URL } from '../../data/content';

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Não foi possível entrar. Tente novamente.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const AdminLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#040D1A] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="grain-overlay" aria-hidden="true" />
      <div className="absolute top-[-20%] right-[-10%] w-[36rem] h-[36rem] spotlight-yellow opacity-50 pointer-events-none" />

      <form
        data-testid="admin-login-form"
        onSubmit={handleSubmit}
        className="relative z-10 w-full max-w-md rounded-3xl border border-amber-400/15 bg-white/[0.04] backdrop-blur-md p-8 sm:p-10"
      >
        <div className="flex flex-col items-center mb-8">
          <img
            src={LOGO_URL}
            alt="Logomarca Leão de Fogo"
            className="w-16 h-16 object-contain drop-shadow-[0_0_16px_rgba(252,211,77,0.4)] mb-4"
          />
          <h1 className="font-chapaza italic font-bold uppercase text-2xl text-white">
            Leão de Fogo
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300/80 mt-2">
            Painel Administrativo
          </p>
        </div>

        <div className="space-y-5">
          <div>
            <Label htmlFor="admin-email" className="text-slate-300 text-xs font-mono uppercase tracking-[0.15em]">
              E-mail
            </Label>
            <Input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-amber-400/60 h-12"
            />
          </div>
          <div>
            <Label htmlFor="admin-password" className="text-slate-300 text-xs font-mono uppercase tracking-[0.15em]">
              Senha
            </Label>
            <Input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-amber-400/60 h-12"
            />
          </div>

          {error && (
            <p
              data-testid="admin-login-error"
              className="text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-4 py-3"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-amber-400 text-[#0A2847] font-display font-bold uppercase tracking-wide text-sm px-8 py-4 rounded-full hover:bg-amber-300 hover:shadow-[0_0_32px_rgba(252,211,77,0.4)] transition-all duration-300 disabled:opacity-60"
          >
            <LogIn className="w-4 h-4" />
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </div>

        <a
          href="/"
          className="block text-center mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-amber-300 transition-colors"
        >
          Voltar ao site
        </a>
      </form>
    </div>
  );
};

export default AdminLogin;
